import type { ReactNode } from 'react'
import { Fragment, useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalContext from 'contexts/ModalContext'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { FlightsClassTypes, RewardFareClass, fareClassToUrlMap } from 'constants/flight'
import { useCharterFilterUrlState } from 'hooks/CharterFlights/useCharterFilterUrlState'
import { getEffectiveCharterBundleRoute } from 'selectors/offerPage/offerPageSelectors'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import MaldivesOnePointPromoModal from 'components/MaldivesDirect/OnePointPromo/MaldivesOnePointPromoModal'
import { ONE_POINT_PROMO_DATE, usePromoValues } from 'components/MaldivesDirect/OnePointPromo/promoContent'
import { getMaldivesBundleCheckOutDate } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectUtils'
import useMaldivesOnePointPromoAvailability from 'components/MaldivesDirect/OnePointPromo/useMaldivesOnePointPromoAvailability'
import { EmptyArray } from 'lib/array/arrayUtils'

const InsetContainer = styled(LayoutContainer)`
  padding-block: ${rem(8)};
`
const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  padding: ${rem(16)};
  border: ${rem(1)} solid ${props => props.theme.palette.messaging.urgency.normalForeground};
  border-radius: ${rem(8)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  ${mediaQueryUp.desktop} {
    flex-direction: row;
    align-items: center;
  }
`
const Copy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(16)};
  flex: 1;
  ${mediaQueryUp.desktop} {
    flex-direction: row;
    align-items: center;
  }
`
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  flex-shrink: 0;
  justify-content: flex-end;
`
const strong = (chunks: ReactNode) => <strong>{chunks}</strong>
const fireIcon = <SolidFireIcon />
const infoIcon = <LineInfoCircleIcon />
const promoDateFormat = { day: 'numeric', month: 'short', year: 'numeric' } as const

interface Props {
  inset?: boolean
  charterBundleRoute?: App.CharterBundleRoute
  checkInDate?: string
  duration: number
  rooms?: Array<App.Occupants>
}

export function MaldivesOfferOnePointPromoBanner({ charterBundleRoute, checkInDate, duration, inset, rooms = EmptyArray }: Props) {
  const intl = useIntl()
  const { fare } = usePromoValues()
  const visible = useMaldivesOnePointPromoAvailability({
    rooms,
    duration,
    disabled: charterBundleRoute !== 'melbourne-maldives' || checkInDate === ONE_POINT_PROMO_DATE,
  })
  const dispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)
  const { setFareClassUrl, setCabinFilter, setRewardSeatsOnly } = useCharterFilterUrlState()

  const onLearnMore = useCallback(async() => {
    if (!visible) return
    const trigger = document.activeElement
    await showModal(<MaldivesOnePointPromoModal />)
    if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus()
  }, [visible, showModal])

  const onCheckAvailability = useCallback(() => {
    if (!visible) return
    setFareClassUrl(fareClassToUrlMap[RewardFareClass.ECONOMY_REWARDS])
    setCabinFilter(FlightsClassTypes.ECONOMY)
    setRewardSeatsOnly(true)
    dispatch({ type: OfferPageActions.setFareClass, fareClass: RewardFareClass.ECONOMY_REWARDS })
    dispatch({
      type: OfferPageActions.setDates,
      checkIn: ONE_POINT_PROMO_DATE,
      checkOut: getMaldivesBundleCheckOutDate(ONE_POINT_PROMO_DATE, duration) ?? undefined,
    })
  }, [visible, duration, dispatch, setFareClassUrl, setCabinFilter, setRewardSeatsOnly])

  if (!visible) return null

  const Container = inset ? InsetContainer : Fragment
  return <Container>
    <Panel data-testid="maldives-offer-one-point-promo">
      <Copy>
        <MessagingLabel variant="urgency" startIcon={fireIcon} endIcon={infoIcon}>
          {intl.formatMessage({
            id: 'jYQfwqOarl9cqAJ4z8Zh',
            defaultMessage: 'Flight sale',
          })}
        </MessagingLabel>
        <BodyText variant="medium">
          {intl.formatMessage({
            id: 'FGpDtemVMsODnkcTyEMe',
            defaultMessage: '<strong>One-way flight to the Maldives for 1 Société Point + {fare} pp.</strong> One departure date only: {date}.',
          }, { strong, fare, date: intlDateFormatter(ONE_POINT_PROMO_DATE, promoDateFormat, intl.locale) })}
        </BodyText>
      </Copy>
      <Actions>
        <TextButton kind="tertiary" size="medium" shape="round" onClick={onLearnMore}>
          {intl.formatMessage({
            id: 'FhgEYad5lYOVFKbQLlbj',
            defaultMessage: 'Learn more',
          })}
        </TextButton>
        <TextButton kind="primary" size="medium" shape="round" onClick={onCheckAvailability}>
          {intl.formatMessage({
            id: 'JQMO0DZtOZtn9TgD0TRC',
            defaultMessage: 'Check availability',
          })}
        </TextButton>
      </Actions>
    </Panel>
  </Container>
}

export default withOfferPageState(state => ({
  charterBundleRoute: getEffectiveCharterBundleRoute(state),
  checkInDate: state.checkInDate,
  duration: state.duration,
  rooms: state.rooms,
}), MaldivesOfferOnePointPromoBanner)
